// REACT & Libraries
import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Save } from "react-feather";
import Swal from "sweetalert2";
import {
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { Btn, H5, P } from "../../../../../../AbstractElements";

// Services
import {
  VIEW_REGION_QUERY_KEY,
  regionService,
} from "../../../../../../Services/regionsService";

import {
  VIEW_COUNTRY_QUERY_KEY,
  countryService,
} from "../../../../../../Services/countryService";

// Types
import { Country } from "../../../../../../Types/MasterData/Country";

// Initial State
const INITIAL_STATE = {
  country: "",
  name: "",
  status: 1,
};

const CreateRegionForm = ({ toggle }: { toggle?: () => void }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(INITIAL_STATE);

  // Get Countries
  const { data: countryData, isLoading: countryIsLoading } = useQuery<{
    data: Country[];
  }>({
    queryKey: VIEW_COUNTRY_QUERY_KEY,
    queryFn: countryService.getAllCountry,
  });

  const countries: Country[] =
    countryData?.data?.filter((item) => item.status === 1) ?? [];

  const createRegionMutation = useMutation({
    mutationFn: regionService.createRegions,
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: VIEW_REGION_QUERY_KEY,
      });
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: result.message,
        confirmButtonColor: "#4e73df",
      });
      setFormData(INITIAL_STATE);
      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message ||
        "Failed to create the region record. Please try again.";
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "error",
        title: "Transaction Failed",
        text: message,
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    Swal.fire({
      title: "Create Region Record?",
      text: "Are you sure you want to create this region record?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: '<i class="fa fa-save"></i> Create New Record',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',
      confirmButtonColor: "#4e73df",
      cancelButtonColor: "#dc3545",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      Swal.fire({
        title: "Creating Region Record...",
        text: "Please wait while the record is being created.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      createRegionMutation.mutate(formData);
    });
  };

  return (
    <ModalBody className="modal-body">
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>
        <Row className="g-3">
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <Row className="p-1">
              <Col md="12">
                <FormGroup>
                  <Label>
                    Country <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-flag txt-primary"></i>
                    </InputGroupText>
                    <Input
                      type="select"
                      id="country"
                      name="country"
                      disabled={countryIsLoading ? true : false}
                      value={
                        countryIsLoading
                          ? "Loading data..."
                          : formData.country
                            ? formData.country
                            : ""
                      }
                      required
                      onChange={handleInputChange}
                    >
                      <option value="">
                        {countryIsLoading ? "Loading..." : "Select Country"}
                      </option>
                      {!countryIsLoading &&
                        countries.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.name}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>
                    Region <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-info-circle txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      minLength={2}
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>
                    Status <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-tag txt-primary"></i>
                    </InputGroupText>

                    <Input
                      className="select-scroll"
                      value={formData.status}
                      id="status"
                      name="status"
                      type="select"
                      required
                      onChange={(e) => {
                        handleInputChange(e);
                      }}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>
          </div>

          <div className="d-flex justify-content-end">
            <Col md="6">
              <Btn
                color="primary"
                className="w-100"
                type="submit"
                disabled={createRegionMutation.isPending}
              >
                <Save size={16} className="me-1" />
                {createRegionMutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Btn>
            </Col>
          </div>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateRegionForm;
